// src/components/product/BundleEditor/BundleShopPricing.jsx
import React, { useState } from 'react';
import { Building2, Percent, AlertTriangle, Loader2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import DiscountPercentagePopup from '../../ui/DiscountPercentagePopup';
import { lineTotal, bundleTotals, missingComponents } from './bundleUtils';
import styles from './BundleEditor.module.css';

const BundleShopPricing = ({ bundleItems, bundleShopIds, shops, priceMaps, loadingMaps, onToggleShop, onPriceChange }) => {
  const { t } = useLocalization();
  const [popup, setPopup] = useState(null); // { anchorEl, shopId, key }

  const isEnabled = (shopId) => bundleShopIds.some(id => String(id) === String(shopId));

  return (
    <div className={styles.pricingSection}>
      <h3 className={styles.sectionTitle}>{t('products.bundleShopPricing')}</h3>
      <div className={styles.shopDataGrid}>
        {shops.map(shop => {
          const priceMap = priceMaps[shop.id];
          const missing = missingComponents(bundleItems, priceMap);
          const blocked = bundleItems.length === 0 || !priceMap || missing.length > 0;
          const enabled = isEnabled(shop.id);
          const totals = enabled ? bundleTotals(bundleItems, shop.id, priceMap) : null;

          return (
            <div key={shop.id} className={`${styles.shopCard} ${enabled ? styles.shopCardEnabled : ''}`}>
              <div className={styles.shopHeader}>
                <label className={styles.shopCheckbox}>
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={blocked && !enabled}
                    onChange={() => onToggleShop(shop.id)}
                  />
                  <Building2 size={16} className={styles.shopIcon} />
                  <span className={styles.shopName}>{shop.name}</span>
                </label>
                {loadingMaps && !priceMap && <Loader2 size={14} className={styles.spinner} />}
              </div>

              {missing.length > 0 && (
                <div className={styles.shopWarning}>
                  <AlertTriangle size={14} />
                  {t('products.componentNotAssigned', { product: missing[0].name })}
                </div>
              )}

              {enabled && (
                <>
                  <div className={styles.priceTableWrapper}>
                    <table className={styles.priceTable}>
                      <thead>
                        <tr>
                          <th>{t('products.component')}</th>
                          <th>{t('products.componentQty')}</th>
                          <th>{t('products.unitValue')}</th>
                          <th>{t('products.lineDiscount')}</th>
                          <th>{t('products.lineTotal')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bundleItems.map(item => {
                          const price = item.shopPrices[shop.id] || { unitValue: '', lineDiscount: 0 };
                          const total = lineTotal(item, shop.id);
                          return (
                            <tr key={item.key}>
                              <td>{item.name}</td>
                              <td>{item.quantity}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={price.unitValue}
                                  onChange={e => onPriceChange(item.key, shop.id, 'unitValue', e.target.value)}
                                />
                              </td>
                              <td>
                                <div className={styles.discountCell}>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={price.lineDiscount}
                                    onChange={e => onPriceChange(item.key, shop.id, 'lineDiscount', e.target.value)}
                                  />
                                  <button
                                    type="button"
                                    className={styles.percentButton}
                                    onClick={e => setPopup({ anchorEl: e.currentTarget, shopId: shop.id, key: item.key })}
                                  >
                                    <Percent size={12} />
                                  </button>
                                </div>
                                {popup && popup.shopId === shop.id && popup.key === item.key && (
                                  <DiscountPercentagePopup
                                    anchorEl={popup.anchorEl}
                                    unitPrice={price.unitValue}
                                    quantity={item.quantity}
                                    onApply={(amount) => {
                                      onPriceChange(item.key, shop.id, 'lineDiscount', amount);
                                      setPopup(null);
                                    }}
                                    onClose={() => setPopup(null)}
                                  />
                                )}
                              </td>
                              <td className={styles.lineTotalCell}>
                                {formatCurrency(total)}
                                {total === 0 && price.unitValue !== '' && (
                                  <span className={styles.freeTag}>{t('products.free')}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.totalsRow}>
                    <span>{t('products.regularTotal')}: {formatCurrency(totals.regularTotal)}</span>
                    <strong>{t('products.bundlePrice')}: {formatCurrency(totals.bundlePrice)}</strong>
                    <span className={styles.savingText}>{t('products.saving')}: {formatCurrency(totals.saving)}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BundleShopPricing;
